import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import ProjectImageWithFallback from "./ProjectImageWithFallback";

interface ProjectCardProps {
  title: string;
  description: string;
  image: string;
  tags: string[];
  link?: string;
  delay?: number;
  id: string;
  isActive?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const ProjectCard = ({
  title,
  description,
  image,
  tags,
  link,
  delay = 0,
  id,
  isActive,
  onMouseEnter,
  onMouseLeave,
}: ProjectCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLAnchorElement>(null);

  // Determine if card should show hover effects (either by mouse hover or mobile active state)
  const showHoverEffect = isActive !== undefined ? isActive : isHovered;

  return (
    <Link
      ref={cardRef}
      to={`/project/${id}`}
      className={cn(
        "group relative border border-white overflow-hidden transition-all duration-700 cursor-pointer block",
        showHoverEffect && "md:scale-[1.03] z-10"
      )}
      onMouseEnter={() => {
        setIsHovered(true);
        onMouseEnter?.();
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        onMouseLeave?.();
      }}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        <ProjectImageWithFallback
          src={image}
          alt={title}
          className={cn(
            "w-full h-full transition-transform duration-700",
            showHoverEffect ? "scale-110" : "scale-100"
          )}
          fallbackText={title}
        />
        <div
          className={cn(
            "absolute inset-0 bg-black transition-opacity duration-500",
            showHoverEffect ? "opacity-30" : "opacity-50"
          )}
        ></div>
      </div>

      {/* Content overlay */}
      <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between transition-all duration-500">
        {/* Top content - Tags */}
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, index) => (
            <span
              key={index}
              className={cn(
                "text-xs uppercase tracking-wider bg-black px-3 py-1 transition-all duration-500",
                showHoverEffect
                  ? "transform translate-y-0 opacity-100"
                  : "transform -translate-y-2 opacity-0"
              )}
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Bottom content */}
        <div
          className={cn(
            "transform transition-all duration-700",
            showHoverEffect
              ? "translate-y-0 opacity-100"
              : "translate-y-4 opacity-80"
          )}
        >
          <h3
            className={cn(
              "font-display text-2xl md:text-3xl mb-2 transition-transform duration-700",
              showHoverEffect ? "translate-x-0" : "-translate-x-4"
            )}
          >
            {title}
          </h3>

          <p
            className={cn(
              "text-sm max-w-md transition-all duration-700 delay-100",
              showHoverEffect
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-4"
            )}
          >
            {description}
          </p>

          <div
            className={cn(
              "mt-4 inline-block transition-all duration-700 delay-200",
              showHoverEffect
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            )}
          >
            <span className="text-sm uppercase tracking-wider border-b border-white pb-1 transition-all duration-300 group-hover:border-opacity-100">
              View Details
            </span>
          </div>
        </div>
      </div>

      {/* Animated corners */}
      <div
        className={cn(
          "absolute top-0 left-0 w-8 h-8 border-t border-l border-white transition-all duration-500",
          showHoverEffect ? "opacity-100 scale-100" : "opacity-0 scale-0"
        )}
        style={{ transformOrigin: "top left" }}
      ></div>

      <div
        className={cn(
          "absolute top-0 right-0 w-8 h-8 border-t border-r border-white transition-all duration-500",
          showHoverEffect ? "opacity-100 scale-100" : "opacity-0 scale-0"
        )}
        style={{ transformOrigin: "top right", transitionDelay: "100ms" }}
      ></div>

      <div
        className={cn(
          "absolute bottom-0 left-0 w-8 h-8 border-b border-l border-white transition-all duration-500",
          showHoverEffect ? "opacity-100 scale-100" : "opacity-0 scale-0"
        )}
        style={{ transformOrigin: "bottom left", transitionDelay: "100ms" }}
      ></div>

      <div
        className={cn(
          "absolute bottom-0 right-0 w-8 h-8 border-b border-r border-white transition-all duration-500",
          showHoverEffect ? "opacity-100 scale-100" : "opacity-0 scale-0"
        )}
        style={{ transformOrigin: "bottom right", transitionDelay: "200ms" }}
      ></div>
    </Link>
  );
};

export default ProjectCard;
